import { CaptureUpdateAction, useExcalidrawAPI } from "@excalidraw/excalidraw";
import { useUIAppState } from "@excalidraw/excalidraw/context/ui-appState";
import { redrawStaticScene } from "@excalidraw/excalidraw/renderer/staticScene";
import { isFrameLikeElement } from "@excalidraw/element";

import { useEffect, useReducer, useRef, useState } from "react";
import { createPortal } from "react-dom";

import type { ExcalidrawElement } from "@excalidraw/element/types";

import {
  getElementAnimations,
  getElementAnimationOrder,
  getElementStartMode,
  withElementAnimations,
} from "../animation/types";
import { buildTimelineSections } from "../animation/layout";
import { getScreens } from "../flipbook/ops";

import {
  animationPresets,
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  presetsByCategory,
  supportsAnchorPick,
  supportsCustomPath,
} from "../animation/presets";
import { makeClip } from "../animation/clips";
import { beginPickAnchor } from "../animation/anchorPick";
import { beginDrawPath } from "../animation/pathDraw";
import { AnimationRuntime } from "../animation/runtime";

import { useActiveClip } from "./ActiveClipContext";
import { AnchorHandle } from "./AnchorHandle";
import { ClipSettings } from "./ClipSettings";
import { ClipTimeline } from "./ClipTimeline";
import { AnimationPreviewWindow } from "./AnimationPreviewWindow";
import { FLIPBOOK_OPEN_EVENT, filmstripIcon } from "./FlipbookBar";

import "./CanvasAnimationBar.scss";

import type { TimelineSection } from "../animation/layout";

import type { ElementAnimation } from "../animation/types";

export const CanvasAnimationBar = () => {
  const excalidrawAPI = useExcalidrawAPI();
  const appState = useUIAppState();
  const {
    activeClip,
    setActiveClip,
    settingsOpen,
    setSettingsOpen,
    previewVisible,
    setPreviewVisible,
  } = useActiveClip();

  // clicking a chip selects the clip and expands the timing panel above the
  // bar — the chip's element is already selected (rows only exist for selected
  // elements), so don't touch selectedElementIds or a multi-selection collapses
  const openClipSettings = (elementId: string, index: number) => {
    setActiveClip({ elementId, index });
    setSettingsOpen(true);
  };
  const runtimeRef = useRef<AnimationRuntime | null>(null);
  const [playState, setPlayState] = useState<"idle" | "playing" | "paused">(
    "idle",
  );
  // ref mirror so the long-lived onChange subscription reads the live value
  const playStateRef = useRef(playState);
  playStateRef.current = playState;
  // element id whose "+" preset picker popover is open
  const [pickerFor, setPickerFor] = useState<string | null>(null);
  // with nothing selected the dock stays collapsed (a big scene would clutter
  // it); this expands it to the full every-animated-element row list
  const [seqExpanded, setSeqExpanded] = useState(false);
  const dockRef = useRef<HTMLDivElement>(null);
  // null = docked above the bar; set once the header is dragged, after which
  // the panel floats free at this fixed position until it's closed
  const [panelPos, setPanelPos] = useState<{ x: number; y: number } | null>(
    null,
  );
  const panelRef = useRef<HTMLDivElement>(null);

  const startPanelDrag = (e: React.PointerEvent) => {
    // drag from the header surface, not its close button
    if ((e.target as HTMLElement).closest("button") || !panelRef.current) {
      return;
    }
    e.preventDefault();
    const rect = panelRef.current.getBoundingClientRect();
    const dx = e.clientX - rect.left;
    const dy = e.clientY - rect.top;
    const onMove = (ev: PointerEvent) =>
      setPanelPos({
        x: Math.max(
          8,
          Math.min(ev.clientX - dx, window.innerWidth - rect.width - 8),
        ),
        y: Math.max(8, Math.min(ev.clientY - dy, window.innerHeight - 80)),
      });
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };

  // clip edits mutate scene customData; useUIAppState only re-renders on
  // appState, so subscribe to scene changes and force a render
  const [, forceRender] = useReducer((n: number) => n + 1, 0);

  // close the preset picker on any pointerdown outside the dock
  useEffect(() => {
    if (!pickerFor) {
      return;
    }
    const onDown = (e: PointerEvent) => {
      if (!dockRef.current?.contains(e.target as Node)) {
        setPickerFor(null);
      }
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [pickerFor]);

  const getRuntime = () => {
    if (!runtimeRef.current) {
      runtimeRef.current = new AnimationRuntime();
    }
    return runtimeRef.current;
  };

  // a playing timeline would otherwise keep ticking (and re-rendering the
  // canvas) after unmount
  useEffect(() => () => runtimeRef.current?.dispose(), []);

  const elements = excalidrawAPI?.getSceneElements() ?? [];
  // the bar is pinned while the animation/settings sidebar tab is open, so
  // clips stay reachable without a selection
  const inAnimationMode =
    appState.openSidebar?.tab === "animation" ||
    appState.openSidebar?.tab === "settings";
  // the sequence timeline's screen sections drive both the timing panel and
  // the dock's ordering view — one derivation so they can't disagree
  const sections = buildTimelineSections(elements, getScreens(elements));
  // rows: the currently selected elements; with nothing selected, fall back
  // to every animated element in section+sequence order (PowerPoint-style
  // ordering view: mode badge + reorder arrows per row)
  const selectedRows = elements.filter(
    (el) => !isFrameLikeElement(el) && appState.selectedElementIds[el.id],
  );
  const sequenceRows: ExcalidrawElement[] = [];
  for (const section of sections) {
    for (const lane of section.lanes) {
      if (
        !isFrameLikeElement(lane.element) &&
        !sequenceRows.some((el) => el.id === lane.element.id)
      ) {
        sequenceRows.push(lane.element);
      }
    }
  }
  const rowElements =
    selectedRows.length > 0 ? selectedRows : seqExpanded ? sequenceRows : [];
  /** first section a row's element belongs to — the context its dock reorder
   * arrows act in */
  const sectionOf = (element: ExcalidrawElement) =>
    sections.find((s) => s.lanes.some((l) => l.element.id === element.id))?.id;
  const animatedCount = elements.filter(
    (el) => getElementAnimations(el).length > 0,
  ).length;

  // the compiled GSAP timeline goes stale the moment a clip is edited —
  // without this flag, pause → edit → resume replays the old timeline
  // (and with loop:true playback never returns to "idle" to recompile)
  const clipsDirtyRef = useRef(false);

  // hot-apply: while playing/paused, rebuild the timeline in place and seek
  // back to the current position so every clip change previews immediately
  // instead of waiting for a stop → play cycle
  // ponytail: full recompile per change; incremental tween patching if
  // recompiles ever get slow on huge scenes
  const hotApply = () => {
    if (!excalidrawAPI) {
      return;
    }
    const runtime = getRuntime();
    const timeMs = runtime.getTimeMs();
    const count = runtime.compile(excalidrawAPI.getSceneElements());
    clipsDirtyRef.current = false;
    if (count === 0) {
      setPlayState("idle");
      excalidrawAPI.triggerRender(true);
      return;
    }
    runtime.play(
      () => {
        // per-tick: draw the static canvas directly — the React setState →
        // throttled-rAF path costs a full extra frame per tick (~30fps cap)
        redrawStaticScene();
        if (!runtime.isPlaying) {
          setPlayState("idle");
          excalidrawAPI.triggerRender(true);
        }
      },
      { loop: true },
    );
    if (playStateRef.current === "paused") {
      runtime.pause();
    }
    runtime.sampleAt(Math.min(timeMs, runtime.getDurationMs()));
    excalidrawAPI.triggerRender(true);
  };
  const hotApplyRef = useRef(hotApply);
  hotApplyRef.current = hotApply;

  // Detect clip changes at the scene boundary, not per call site: clips are
  // scene data mutated from many places (this bar, the sidebar gallery, the
  // customize window, drag-drop, undo/redo, collab). Any of them must
  // invalidate the compiled timeline, or elements animated after play was
  // pressed stay static — the loop never returns to "idle" to recompile.
  // ponytail: JSON signature per scene change; keyed cache if it ever shows
  // up in profiles
  const animSigRef = useRef<string | null>(null);
  useEffect(() => {
    if (!excalidrawAPI) {
      return;
    }
    return excalidrawAPI.onChange(() => {
      let sig = "";
      for (const el of excalidrawAPI.getSceneElements()) {
        const clips = getElementAnimations(el);
        if (clips.length > 0) {
          // element-level sequencing fields must be in the signature too, or
          // mode/reorder edits won't hot-apply while playing
          sig += `${el.id}:${getElementStartMode(
            el,
          )}:${getElementAnimationOrder(el)}${JSON.stringify(clips)}`;
        }
      }
      if (animSigRef.current !== null && sig !== animSigRef.current) {
        clipsDirtyRef.current = true;
        if (playStateRef.current !== "idle") {
          hotApplyRef.current();
        }
      }
      animSigRef.current = sig;
      forceRender();
    });
  }, [excalidrawAPI]);

  const patchCustomData = (
    element: ExcalidrawElement,
    customData: Record<string, any>,
    opts: { capture?: boolean } = {},
  ) => {
    if (!excalidrawAPI) {
      return;
    }
    clipsDirtyRef.current = true;
    excalidrawAPI.mutateElement(element, { customData });
    // mutateElement alone never records an undo entry — without an explicit
    // capture the edit gets folded into the next unrelated undo step.
    // Drags pass capture: false per frame and capture once on release.
    if (opts.capture !== false) {
      excalidrawAPI.updateScene({
        elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
        captureUpdate: CaptureUpdateAction.IMMEDIATELY,
      });
    }
    // the onChange signature pass hot-applies this mutation while playing
  };

  const updateClips = (
    element: ExcalidrawElement,
    clips: ReturnType<typeof getElementAnimations>,
    opts: { capture?: boolean } = {},
  ) => {
    patchCustomData(element, withElementAnimations(element, clips), opts);
  };

  const addClip = (element: ExcalidrawElement, presetId: string) => {
    const clips = getElementAnimations(element);
    const next = [...clips, makeClip(presetId)];
    updateClips(element, next);
    setActiveClip({ elementId: element.id, index: next.length - 1 });
    setSettingsOpen(true);
    // followPath without a path in reach → let the user draw one right away
    if (presetId === "followPath" && excalidrawAPI) {
      beginDrawPath(excalidrawAPI, [
        { elementId: element.id, index: next.length - 1 },
      ]);
    }
  };

  const removeClip = (element: ExcalidrawElement, index: number) => {
    const clips = getElementAnimations(element);
    updateClips(
      element,
      clips.filter((_, i) => i !== index),
    );
    if (activeClip?.elementId === element.id) {
      if (activeClip.index === index) {
        setActiveClip(null);
        setSettingsOpen(false);
      } else if (activeClip.index > index) {
        // removing an earlier clip shifts the active one down a slot
        setActiveClip({ elementId: element.id, index: activeClip.index - 1 });
      }
    }
  };

  const updateClipAt = (
    element: ExcalidrawElement,
    index: number,
    updates: Partial<ElementAnimation>,
    opts: { capture?: boolean } = {},
  ) => {
    const clips = getElementAnimations(element);
    updateClips(
      element,
      clips.map((clip, i) =>
        i === index
          ? {
              ...clip,
              ...updates,
              params: { ...clip.params, ...updates.params },
            }
          : clip,
      ),
      opts,
    );
  };

  const toggleStartMode = (element: ExcalidrawElement, index: number) => {
    const clips = getElementAnimations(element);
    updateClips(
      element,
      clips.map((clip, i) =>
        i === index
          ? { ...clip, startMode: clip.startMode === "with" ? "after" : "with" }
          : clip,
      ),
    );
  };

  const toggleElementStartMode = (element: ExcalidrawElement) => {
    patchCustomData(element, {
      ...element.customData,
      animationStartMode:
        getElementStartMode(element) === "with" ? "after" : "with",
    });
  };

  /** Swap the element with its neighbor within its section's lanes, then
   * compact-rewrite animationOrder over every lane in display order (screens
   * first, canvas last) — one pass that also self-heals absent/duplicate keys
   * (collab). An element shown in two sections keeps its first slot. One undo
   * capture. */
  const moveLane = (
    element: ExcalidrawElement,
    dir: -1 | 1,
    sectionId: string,
  ) => {
    if (!excalidrawAPI) {
      return;
    }
    const els = excalidrawAPI.getSceneElements();
    const lists = buildTimelineSections(els, getScreens(els)).map((s) => ({
      id: s.id,
      order: s.lanes.map((l) => l.element),
    }));
    const list = lists.find((s) => s.id === sectionId)?.order;
    if (!list) {
      return;
    }
    const i = list.findIndex((el) => el.id === element.id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= list.length) {
      return;
    }
    [list[i], list[j]] = [list[j], list[i]];
    clipsDirtyRef.current = true;
    const seen = new Set<string>();
    let idx = 0;
    for (const s of lists) {
      for (const el of s.order) {
        if (seen.has(el.id)) {
          continue;
        }
        seen.add(el.id);
        excalidrawAPI.mutateElement(el, {
          customData: { ...el.customData, animationOrder: idx++ },
        });
      }
    }
    excalidrawAPI.updateScene({
      elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    });
  };

  const moveClip = (element: ExcalidrawElement, index: number, dir: -1 | 1) => {
    const clips = getElementAnimations(element);
    const target = index + dir;
    if (target < 0 || target >= clips.length) {
      return;
    }
    const next = [...clips];
    [next[index], next[target]] = [next[target], next[index]];
    updateClips(element, next);
    setActiveClip({ elementId: element.id, index: target });
  };

  // scrubbing seeks a compiled GSAP timeline directly, so every preset/
  // template previews identically to playback and export. Each ruler scrubs
  // ITS screen: compile that section's members (the same set Studio/export
  // compile) and sample on the section's local clock.
  const onScrubStart = (section: TimelineSection) => {
    const runtime = getRuntime();
    if (playState === "playing") {
      runtime.pause();
      setPlayState("paused");
      return;
    }
    runtime.compile(section.members);
    // a section compile is not the whole-scene timeline — the play button
    // must take the idle path and rebuild
    clipsDirtyRef.current = true;
    setPlayState("idle");
  };

  const onScrub = (timeMs: number) => {
    getRuntime().sampleAt(timeMs);
    redrawStaticScene();
  };

  const onPlayPause = () => {
    const runtime = getRuntime();
    // per-tick: direct canvas redraw, not the React setState → throttled-rAF
    // path (costs an extra frame per tick — ~30fps cap)
    const tick = () => redrawStaticScene();
    if (playState === "playing") {
      runtime.pause();
      setPlayState("paused");
      return;
    }
    if (playState === "paused" && !clipsDirtyRef.current) {
      runtime.resume();
      setPlayState("playing");
      return;
    }
    // idle, or paused with edited clips (the old timeline's position is
    // meaningless after durations/presets changed) — rebuild and start over
    const count = runtime.compile(excalidrawAPI?.getSceneElements() ?? []);
    clipsDirtyRef.current = false;
    if (count === 0) {
      setPlayState("idle");
      return;
    }
    setPlayState("playing");
    runtime.play(
      () => {
        tick();
        if (!runtime.isPlaying) {
          setPlayState("idle");
          excalidrawAPI?.triggerRender(true);
        }
      },
      { loop: true },
    );
  };

  // the expanded panel edits the active clip; the timeline shows every
  // animated element, so look the active one up scene-wide, not per row
  const activeElement = activeClip
    ? elements.find((el) => el.id === activeClip.elementId)
    : undefined;
  const activeClips = activeElement ? getElementAnimations(activeElement) : [];
  const activeClipObj = activeClip ? activeClips[activeClip.index] : undefined;
  // a clip loaded into the settings form; the panel can also open timeline-only
  const hasSettings = !!activeElement && !!activeClipObj;
  const panelOpen = settingsOpen && (hasSettings || animatedCount > 0);

  // closing the panel re-docks it — reopening starts above the bar again
  useEffect(() => {
    if (!panelOpen) {
      setPanelPos(null);
    }
  }, [panelOpen]);

  // scrubbing from idle registers animation overrides on the canvas; if the
  // timing panel closes without a play→stop cycle they'd stay frozen forever
  useEffect(() => {
    if (!panelOpen && playState === "idle") {
      runtimeRef.current?.dispose();
      excalidrawAPI?.triggerRender(true);
    }
  }, [panelOpen, playState, excalidrawAPI]);

  // keep the bar mounted while playing/paused even with nothing selected —
  // hiding it would strand a looping (or frozen paused) animation with no
  // visible pause/stop control
  if (rowElements.length === 0 && !inAnimationMode && playState === "idle") {
    return null;
  }

  const pickerElement = pickerFor
    ? rowElements.find((el) => el.id === pickerFor)
    : undefined;

  // once dragged, the panel portals to <body>: the dock's translateX(-50%)
  // makes the dock the containing block for position:fixed, so a fixed
  // panel left inside it would be offset by the dock's own position
  const panelNode = panelOpen && (
    <div
      className="canvas-animation-panel"
      ref={panelRef}
      style={
        panelPos
          ? {
              position: "fixed",
              left: panelPos.x,
              top: panelPos.y,
              zIndex: 55,
            }
          : undefined
      }
    >
      <div
        className="canvas-animation-panel__header"
        onPointerDown={startPanelDrag}
      >
        <span>
          {hasSettings
            ? `${
                animationPresets[activeClipObj!.preset]?.name ??
                activeClipObj!.preset
              } — timing & settings`
            : "Sequence timeline"}
          {playState === "playing" && (
            <span
              className="canvas-animation-panel__live"
              title="Edits apply to the playing animation in real time"
            >
              live
            </span>
          )}
        </span>
        <button onClick={() => setSettingsOpen(false)}>×</button>
      </div>
      <ClipTimeline
        sections={sections}
        activeClip={activeClip}
        playing={playState === "playing"}
        onSelect={(elementId, index) => setActiveClip({ elementId, index })}
        onUpdate={(elementId, index, updates, commit) => {
          const el = elements.find((e) => e.id === elementId);
          if (el) {
            updateClipAt(el, index, updates, { capture: commit !== false });
          }
        }}
        onToggleMode={toggleElementStartMode}
        onMoveLane={moveLane}
        onScrubStart={onScrubStart}
        onScrub={onScrub}
        getTimeMs={() => getRuntime().getTimeMs()}
      />
      {hasSettings && (
        <div className="canvas-animation-panel__body animation-panel">
          <ClipSettings
            clip={activeClipObj!}
            element={activeElement!}
            elements={elements}
            appState={appState}
            onUpdate={(updates) =>
              updateClipAt(activeElement!, activeClip!.index, updates)
            }
            onRemove={() => removeClip(activeElement!, activeClip!.index)}
            pathTools={
              activeClipObj!.preset === "followPath" && excalidrawAPI
                ? {
                    boundTo:
                      elements.find(
                        (el) => el.id === activeClipObj!.pathElementId,
                      )?.type ?? null,
                    onDrawPath: () =>
                      beginDrawPath(excalidrawAPI, [
                        {
                          elementId: activeElement!.id,
                          index: activeClip!.index,
                        },
                      ]),
                  }
                : undefined
            }
            onDrawMorphTarget={
              excalidrawAPI && supportsCustomPath(activeClipObj!.preset)
                ? () =>
                    beginDrawPath(
                      excalidrawAPI,
                      [
                        {
                          elementId: activeElement!.id,
                          index: activeClip!.index,
                        },
                      ],
                      "morphTarget",
                    )
                : undefined
            }
            onPickAnchor={
              excalidrawAPI && supportsAnchorPick(activeClipObj!.preset)
                ? () =>
                    beginPickAnchor(excalidrawAPI, [
                      {
                        elementId: activeElement!.id,
                        index: activeClip!.index,
                      },
                    ])
                : undefined
            }
          />
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className="canvas-animation-dock" ref={dockRef}>
        {!panelPos && panelNode}

        {pickerElement && (
          <div className="canvas-animation-picker">
            {CATEGORY_ORDER.map((category) => {
              const presets = presetsByCategory(category);
              if (presets.length === 0) {
                return null;
              }
              return (
                <div key={category}>
                  <div className="canvas-animation-picker__category">
                    {CATEGORY_LABELS[category]}
                  </div>
                  <div className="canvas-animation-picker__presets">
                    {presets.map((preset) => (
                      <button
                        key={preset.id}
                        onClick={() => {
                          addClip(pickerElement, preset.id);
                          setPickerFor(null);
                        }}
                      >
                        {preset.name}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="canvas-animation-bar">
          <button
            className={`canvas-animation-bar__play-btn${
              playState === "playing"
                ? " canvas-animation-bar__play-btn--playing"
                : ""
            }`}
            onClick={onPlayPause}
            disabled={animatedCount === 0}
            title={
              playState === "playing"
                ? "Pause"
                : playState === "paused"
                ? "Resume"
                : "Play"
            }
          >
            <span className="canvas-animation-bar__play-icon">
              {playState === "playing" ? "❚❚" : "▶"}
            </span>
          </button>

          <button
            className="canvas-animation-bar__play-btn"
            title="Flipbook — draw frame-by-frame animation"
            onClick={() =>
              window.dispatchEvent(new CustomEvent(FLIPBOOK_OPEN_EVENT))
            }
          >
            <span className="canvas-animation-bar__play-icon">
              {filmstripIcon}
            </span>
          </button>

          <button
            className="canvas-animation-bar__play-btn"
            onClick={() => setPreviewVisible(!previewVisible)}
            // the preview can also flip through screens/flipbook pages, so
            // frames alone are enough to open it
            disabled={animatedCount === 0 && !elements.some(isFrameLikeElement)}
            title={previewVisible ? "Close preview" : "Open preview"}
            style={{
              background: previewVisible
                ? "var(--toolbar-active-bg, #0a84ff)"
                : "transparent",
            }}
          >
            <span className="canvas-animation-bar__play-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                width="18"
                height="18"
                fill="currentColor"
              >
                <path d="M5.127 3.502L5.25 3.5h9.5q.062 0 .123.002A2.25 2.25 0 0 0 12.75 2h-5.5a2.25 2.25 0 0 0-2.123 1.502M1 10.25A2.25 2.25 0 0 1 3.25 8h13.5A2.25 2.25 0 0 1 19 10.25v5.5A2.25 2.25 0 0 1 16.75 18H3.25A2.25 2.25 0 0 1 1 15.75zM3.25 6.5l-.123.002A2.25 2.25 0 0 1 5.25 5h9.5c.98 0 1.814.627 2.123 1.502L16.75 6.5z" />
              </svg>
            </span>
          </button>

          <button
            className="canvas-animation-bar__play-btn"
            onClick={() => setSettingsOpen(!settingsOpen)}
            disabled={animatedCount === 0}
            title={panelOpen ? "Close timeline" : "Timeline — all animations"}
            style={{
              background: panelOpen
                ? "var(--toolbar-active-bg, #0a84ff)"
                : "transparent",
            }}
          >
            <span className="canvas-animation-bar__play-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                width="18"
                height="18"
                fill="currentColor"
              >
                <rect x="2" y="4" width="10" height="3" rx="1" />
                <rect x="6" y="9" width="12" height="3" rx="1" />
                <rect x="4" y="14" width="8" height="3" rx="1" />
              </svg>
            </span>
          </button>

          <div
            className="canvas-animation-bar__rows"
            // double-click anywhere on the rows opens the full timeline panel
            onDoubleClick={() => {
              if (animatedCount > 0) {
                setSettingsOpen(true);
              }
            }}
          >
            {rowElements.length === 0 && animatedCount === 0 && (
              <span className="canvas-animation-bar__hint">
                Select an element to animate
              </span>
            )}
            {selectedRows.length === 0 && animatedCount > 0 && (
              <button
                className="canvas-animation-bar__expand"
                title={
                  seqExpanded
                    ? "Collapse"
                    : "Show every animated element in play order"
                }
                onClick={() => setSeqExpanded(!seqExpanded)}
              >
                {seqExpanded ? "⌃" : `${animatedCount} animated ⌄`}
              </button>
            )}
            {rowElements.map((el) => {
              const clips = getElementAnimations(el);
              const seqIndex = sequenceRows.indexOf(el);
              const startMode = getElementStartMode(el);
              return (
                <div key={el.id} className="canvas-animation-bar__row">
                  {seqIndex >= 0 && sequenceRows.length > 1 && (
                    <span className="canvas-animation-bar__seq">
                      <button
                        className="canvas-animation-bar__clip-arrow"
                        title="Play earlier in the sequence"
                        onClick={() => {
                          const sid = sectionOf(el);
                          if (sid) {
                            moveLane(el, -1, sid);
                          }
                        }}
                      >
                        ‹
                      </button>
                      <span className="canvas-animation-bar__seq-num">
                        {seqIndex + 1}
                      </span>
                      <button
                        className="canvas-animation-bar__clip-arrow"
                        title="Play later in the sequence"
                        onClick={() => {
                          const sid = sectionOf(el);
                          if (sid) {
                            moveLane(el, 1, sid);
                          }
                        }}
                      >
                        ›
                      </button>
                      {seqIndex > 0 && (
                        <button
                          className="canvas-animation-bar__clip-badge"
                          title={
                            startMode === "with"
                              ? "Starts with the previous element — click to play after it finishes"
                              : "Starts after the previous element finishes — click to play alongside it"
                          }
                          onClick={() => toggleElementStartMode(el)}
                        >
                          {startMode === "with" ? "∥" : "→"}
                        </button>
                      )}
                    </span>
                  )}
                  {(rowElements.length > 1 || selectedRows.length === 0) && (
                    <span
                      className="canvas-animation-bar__row-label"
                      title={el.type}
                    >
                      {el.type}
                    </span>
                  )}
                  <div className="canvas-animation-bar__clips">
                    {clips.map((clip, index) => {
                      const isActive =
                        activeClip?.elementId === el.id &&
                        activeClip.index === index;
                      return (
                        <div
                          key={index}
                          className={`canvas-animation-bar__clip${
                            isActive
                              ? " canvas-animation-bar__clip--active"
                              : ""
                          }`}
                        >
                          {isActive && index > 0 && (
                            <button
                              className="canvas-animation-bar__clip-arrow"
                              title="Move earlier"
                              onClick={() => moveClip(el, index, -1)}
                            >
                              ‹
                            </button>
                          )}
                          {index > 0 && (
                            <button
                              className="canvas-animation-bar__clip-badge"
                              title={
                                clip.startMode === "with"
                                  ? "Starts with previous — click to sequence after"
                                  : "Starts after previous — click to overlap"
                              }
                              onClick={() => toggleStartMode(el, index)}
                            >
                              {clip.startMode === "with" ? "∥" : "→"}
                            </button>
                          )}
                          <button
                            className="canvas-animation-bar__clip-name"
                            title={
                              animationPresets[clip.preset]?.name ?? clip.preset
                            }
                            onClick={() => openClipSettings(el.id, index)}
                          >
                            {animationPresets[clip.preset]?.name ?? clip.preset}
                          </button>
                          <button
                            className="canvas-animation-bar__clip-remove"
                            title="Remove"
                            onClick={() => removeClip(el, index)}
                          >
                            ×
                          </button>
                          {isActive && index < clips.length - 1 && (
                            <button
                              className="canvas-animation-bar__clip-arrow"
                              title="Move later"
                              onClick={() => moveClip(el, index, 1)}
                            >
                              ›
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <button
                    className="canvas-animation-bar__add"
                    title="Add animation"
                    onClick={() =>
                      setPickerFor(pickerFor === el.id ? null : el.id)
                    }
                  >
                    +
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {panelPos ? createPortal(panelNode, document.body) : null}
      {panelOpen &&
        hasSettings &&
        excalidrawAPI &&
        supportsAnchorPick(activeClipObj!.preset) && (
          <AnchorHandle
            element={activeElement!}
            pivotX={activeClipObj!.params.pivotX ?? 0.5}
            pivotY={activeClipObj!.params.pivotY ?? 0.5}
            api={excalidrawAPI}
            onChange={(pivot, commit) =>
              updateClipAt(
                activeElement!,
                activeClip!.index,
                { params: pivot },
                { capture: commit },
              )
            }
          />
        )}
      {previewVisible && (
        <AnimationPreviewWindow
          runtime={runtimeRef.current}
          isPlaying={playState === "playing"}
        />
      )}
    </>
  );
};
